import { useCallback, useEffect, useState } from 'react';
import {
  addDocSigner,
  cancelSignRequests,
  getDocSigningStatus,
  getEditableDoc,
  removeDocSigner,
  saveDocBody,
  setCaseName,
  setCaseNumber,
  updateDocSigner,
  SidebarDocError,
  type DocSigner,
  type EditableDoc,
} from '../api/sidebarDocService';
import { PreviewFrame } from './PreviewFrame';
import { confirmDialog } from '../lib/confirmDialog';

/**
 * In-Zoom document management (Danny, 2026-09-30): brings the desktop
 * editor's capabilities into the sidebar so mediators never leave Zoom to
 * finalize a stipulation — edit/add/remove signers, correct the authoritative
 * case number + title, cancel a signature request (clears ALL signatures →
 * recall → edit → resend), and preview + edit the FINAL rendered document
 * that goes to the parties and the court.
 */

const EDITABLE_STATUSES = ['draft', 'review', 'approved'];

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof SidebarDocError) return e.message;
  return e instanceof Error ? e.message : fallback;
}

export function DocManagementPanel({
  agreementId,
  caseId,
  accessToken,
  onChanged,
}: {
  agreementId: string;
  caseId: string;
  accessToken: string;
  /** Let the parent refresh its own (session-based) signing status. */
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [signers, setSigners] = useState<DocSigner[]>([]);
  const [docStatus, setDocStatus] = useState<string>('');
  const [editable, setEditable] = useState<EditableDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const locked = docStatus !== '' && !EDITABLE_STATUSES.includes(docStatus);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [ss, ed] = await Promise.all([
        getDocSigningStatus(accessToken, agreementId),
        getEditableDoc(accessToken, agreementId).catch(() => null),
      ]);
      setSigners(ss.signers ?? []);
      setDocStatus(ss.agreement_status ?? ed?.status ?? '');
      if (ed) setEditable(ed);
    } catch (e) {
      setErr(errMsg(e, 'Could not load document details.'));
    } finally {
      setLoading(false);
    }
  }, [accessToken, agreementId]);

  useEffect(() => {
    if (open && !signers.length && !loading) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const after = async (msg?: string) => {
    if (msg) {
      setNotice(msg);
      setTimeout(() => setNotice(null), 4000);
    }
    await refresh();
    onChanged();
  };

  if (!open) {
    return (
      <div className="mt-4 border-t border-surface-border pt-3">
        <button type="button" className="text-xs font-medium text-brand hover:underline" onClick={() => setOpen(true)}>
          ⚙ Manage document, signers & final version
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4 rounded-md border border-surface-border bg-gray-50 p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Manage document</div>
        <button type="button" className="text-xs text-gray-400 hover:text-brand" onClick={() => setOpen(false)}>
          Hide
        </button>
      </div>

      {err && <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{err}</div>}
      {notice && <div className="rounded border border-green-200 bg-green-50 px-2 py-1.5 text-xs text-green-800">{notice}</div>}
      {loading && <div className="text-xs text-gray-500">Loading…</div>}

      {locked && (
        <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          This document is <span className="font-mono">{docStatus}</span>. Terms, caption, and signed parties are locked.
          Use <span className="font-semibold">Cancel signature request</span> below to recall it — that clears every
          signature so all parties re-sign — then edit and resend.
        </div>
      )}

      {/* #4 / #5 — authoritative case number + title */}
      <CaseIdentityEditor
        caseId={caseId}
        accessToken={accessToken}
        currentNumber={editable?.fields?.case_no ?? ''}
        onDone={(m) => void after(m)}
        setBusy={setBusy}
        busy={busy}
        setErr={setErr}
      />

      {/* Caption boxes on the court form — parties, division, courtroom, printed
          date. Stipulations only (the editable `fields` are the court caption);
          hidden once the doc is locked. Mirrors the dashboard full-page editor
          so the mediator can fix the top-of-form boxes from the final view in
          the sidebar (Danny, 2026-10-16). */}
      {!locked && editable?.doc_type === 'stipulation' && editable.fields && (
        <CaptionDetailsEditor
          agreementId={agreementId}
          accessToken={accessToken}
          fields={editable.fields}
          captionOverrides={editable.caption_overrides}
          onDone={(m) => void after(m)}
          setBusy={setBusy}
          busy={busy}
          setErr={setErr}
        />
      )}

      {/* #1 / #2 / #3 — signer management */}
      <SignerManager
        agreementId={agreementId}
        accessToken={accessToken}
        signers={signers}
        onDone={(m) => void after(m)}
        setBusy={setBusy}
        busy={busy}
        setErr={setErr}
      />

      {/* #6 — cancel signature request (clears all, recall → edit → resend) */}
      <div>
        <div className="section-label">Cancel signature request</div>
        <p className="mt-1 text-[11px] text-gray-500">
          Clears <span className="font-semibold">all</span> collected signatures, kills every signing link, and reopens
          the draft for editing. Everyone who signed must re-sign.
        </p>
        <button
          type="button"
          className="btn-secondary mt-1.5 !py-1 !text-xs !text-red-700"
          disabled={busy !== null}
          onClick={async () => {
            const anySigned = signers.some((s) => s.status === 'signed');
            const msg = anySigned
              ? 'Cancel the signature request?\n\nThis CLEARS every signature already collected — all parties will have to re-sign — and reopens the draft for editing.'
              : 'Cancel the signature request?\n\nThis kills every outstanding signing link and reopens the draft for editing.';
            if (!(await confirmDialog(msg, { danger: true, confirmLabel: 'Clear signatures' }))) return;
            setBusy('cancel');
            setErr(null);
            try {
              const reopenSigned = docStatus === 'signed' || signers.every((s) => s.status === 'signed');
              const r = await cancelSignRequests(accessToken, agreementId, { reopenSigned });
              await after(`Signatures cleared (${r.signatures_reset}). The draft is editable again.`);
            } catch (e) {
              setErr(errMsg(e, 'Could not cancel the signature request.'));
            } finally {
              setBusy(null);
            }
          }}
        >
          {busy === 'cancel' ? 'Cancelling…' : 'Cancel signature request & clear signatures'}
        </button>
      </div>

      {/* #7 — final rendered version + editable terms */}
      <FinalVersion
        agreementId={agreementId}
        accessToken={accessToken}
        editable={editable}
        locked={locked}
        onLoad={refresh}
        onSaved={(m) => void after(m)}
        setBusy={setBusy}
        busy={busy}
        setErr={setErr}
      />
    </div>
  );
}

// ── Caption details (stipulation court-form header boxes) ──────────────────
function CaptionDetailsEditor({
  agreementId,
  accessToken,
  fields,
  captionOverrides,
  onDone,
  setBusy,
  busy,
  setErr,
}: {
  agreementId: string;
  accessToken: string;
  fields: NonNullable<EditableDoc['fields']>;
  captionOverrides: Record<string, unknown> | null;
  onDone: (msg?: string) => void;
  setBusy: (v: string | null) => void;
  busy: string | null;
  setErr: (v: string | null) => void;
}) {
  const seed = useCallback(
    () => ({
      case_no: fields.case_no ?? '',
      plaintiff: fields.plaintiff ?? '',
      defendants: (fields.defendants ?? []).join('\n'),
      dated_on: fields.dated_on ?? '',
      division: fields.division ?? '',
      courtroom: fields.courtroom ?? '',
    }),
    [fields],
  );
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(seed);
  // Re-seed from the effective values whenever the doc reloads.
  useEffect(() => setF(seed()), [seed]);

  if (!open) {
    return (
      <div>
        <div className="section-label">Caption details (this document)</div>
        <button type="button" className="mt-1 text-xs text-brand hover:underline" onClick={() => setOpen(true)}>
          Edit parties, division &amp; courtroom…
        </button>
      </div>
    );
  }

  const field = (
    label: string,
    key: keyof ReturnType<typeof seed>,
    opts: { textarea?: boolean; type?: string } = {},
  ) => (
    <label className="text-[11px] text-gray-500">
      {label}
      {opts.textarea ? (
        <textarea
          rows={2}
          className="mt-0.5 w-full rounded border border-surface-border px-1.5 py-1 text-xs"
          value={f[key]}
          onChange={(e) => setF((x) => ({ ...x, [key]: e.target.value }))}
        />
      ) : (
        <input
          type={opts.type ?? 'text'}
          className="mt-0.5 w-full rounded border border-surface-border px-1.5 py-1 text-xs"
          value={f[key]}
          onChange={(e) => setF((x) => ({ ...x, [key]: e.target.value }))}
        />
      )}
    </label>
  );

  return (
    <div>
      <div className="section-label">Caption details (this document)</div>
      <div className="mt-1.5 space-y-2 rounded-md border border-surface-border bg-white p-2.5">
        <div className="grid gap-2 sm:grid-cols-2">
          {field('Case number', 'case_no')}
          {field('Dated (printed date)', 'dated_on', { type: 'date' })}
          {field('Plaintiff(s)', 'plaintiff')}
          {field('Defendant(s) — one per line', 'defendants', { textarea: true })}
          {field('Division', 'division')}
          {field('Courtroom', 'courtroom')}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary !py-1 !text-xs"
            disabled={busy !== null}
            onClick={async () => {
              setBusy('caption');
              setErr(null);
              try {
                await saveDocBody(accessToken, agreementId, {
                  caption_overrides: {
                    ...(captionOverrides ?? {}),
                    case_no: f.case_no.trim(),
                    plaintiff: f.plaintiff.trim(),
                    defendants: f.defendants.split('\n').map((x) => x.trim()).filter(Boolean),
                    dated_on: f.dated_on,
                    division: f.division.trim() || null,
                    courtroom: f.courtroom.trim() || null,
                  },
                });
                setOpen(false);
                onDone('Caption details updated.');
              } catch (e) {
                setErr(errMsg(e, 'Could not save the caption details.'));
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'caption' ? 'Saving…' : 'Save caption details'}
          </button>
          <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
        <p className="text-[11px] text-gray-400">
          Applies to this document only. For a case-wide change, use Case identity above.
        </p>
      </div>
    </div>
  );
}

// ── #4 / #5 ───────────────────────────────────────────────────────────────
function CaseIdentityEditor({
  caseId,
  accessToken,
  currentNumber,
  onDone,
  setBusy,
  busy,
  setErr,
}: {
  caseId: string;
  accessToken: string;
  currentNumber: string;
  onDone: (msg?: string) => void;
  setBusy: (v: string | null) => void;
  busy: string | null;
  setErr: (v: string | null) => void;
}) {
  const [num, setNum] = useState(currentNumber);
  const [title, setTitle] = useState('');
  useEffect(() => setNum(currentNumber), [currentNumber]);

  return (
    <div>
      <div className="section-label">Case identity (updates the case everywhere)</div>
      <div className="mt-1.5 space-y-2">
        <div className="flex items-end gap-1.5">
          <label className="flex-1">
            <span className="text-[11px] text-gray-500">Case number</span>
            <input
              className="mt-0.5 w-full rounded-md border border-surface-border px-1.5 py-1 text-xs"
              value={num}
              onChange={(e) => setNum(e.target.value)}
              placeholder="e.g. 26S396"
            />
          </label>
          <button
            type="button"
            className="btn-secondary !py-1 !text-xs"
            disabled={busy !== null || !num.trim() || num.trim() === currentNumber}
            onClick={async () => {
              setBusy('casenum');
              setErr(null);
              try {
                await setCaseNumber(accessToken, caseId, num.trim());
                onDone('Case number updated.');
              } catch (e) {
                setErr(errMsg(e, 'Could not update the case number.'));
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'casenum' ? '…' : 'Save'}
          </button>
        </div>
        <div className="flex items-end gap-1.5">
          <label className="flex-1">
            <span className="text-[11px] text-gray-500">Case title / caption</span>
            <input
              className="mt-0.5 w-full rounded-md border border-surface-border px-1.5 py-1 text-xs"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Mo Vergara v. Rob Tenant"
            />
          </label>
          <button
            type="button"
            className="btn-secondary !py-1 !text-xs"
            disabled={busy !== null || !title.trim()}
            onClick={async () => {
              setBusy('casename');
              setErr(null);
              try {
                await setCaseName(accessToken, caseId, title.trim());
                setTitle('');
                onDone('Case title updated.');
              } catch (e) {
                setErr(errMsg(e, 'Could not update the case title.'));
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === 'casename' ? '…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── #1 / #2 / #3 ────────────────────────────────────────────────────────────
function SignerManager({
  agreementId,
  accessToken,
  signers,
  onDone,
  setBusy,
  busy,
  setErr,
}: {
  agreementId: string;
  accessToken: string;
  signers: DocSigner[];
  onDone: (msg?: string) => void;
  setBusy: (v: string | null) => void;
  busy: string | null;
  setErr: (v: string | null) => void;
}) {
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '' });
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', role: '', email: '', phone: '' });

  const startEdit = (s: DocSigner) => {
    setEditId(s.id);
    setForm({ name: s.signer_name, role: s.role_label ?? '', email: s.signer_email ?? '', phone: s.signer_phone ?? '' });
  };

  return (
    <div>
      <div className="section-label">Signers</div>
      <ul className="mt-1.5 space-y-1.5">
        {signers.map((s) => {
          const signed = s.status === 'signed';
          if (editId === s.id) {
            return (
              <li key={s.id} className="rounded border border-surface-border bg-white p-2">
                <div className="grid grid-cols-2 gap-1.5">
                  <input className="col-span-2 rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Signing name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input className="rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
                  <input className="rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input className="col-span-2 rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    className="btn-primary !py-0.5 !text-xs"
                    disabled={busy !== null || !form.name.trim()}
                    onClick={async () => {
                      setBusy('editsigner');
                      setErr(null);
                      try {
                        await updateDocSigner(accessToken, agreementId, s.id, {
                          name: form.name.trim(),
                          role_label: form.role.trim() || undefined,
                          email: form.email.trim() || undefined,
                          phone: form.phone.trim() || undefined,
                        });
                        setEditId(null);
                        onDone('Signer updated.');
                      } catch (e) {
                        setErr(errMsg(e, 'Could not update the signer.'));
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    {busy === 'editsigner' ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="btn-secondary !py-0.5 !text-xs" onClick={() => setEditId(null)}>
                    Cancel
                  </button>
                </div>
              </li>
            );
          }
          return (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded border border-surface-border bg-white px-2 py-1.5">
              <div className="min-w-0">
                <div className="truncate text-xs font-medium">
                  {s.signer_name}
                  {s.role_label ? <span className="text-gray-500"> — {s.role_label}</span> : null}
                </div>
                <div className="text-[10px] text-gray-400">
                  {s.status}
                  {(s.signer_email || s.signer_phone) && ` · ${[s.signer_email, s.signer_phone].filter(Boolean).join(' · ')}`}
                </div>
                {s.status === 'pending' && !s.party_id && !s.signer_email && !s.signer_phone && (
                  <div className="mt-0.5 text-[10px] font-medium text-amber-700">⚠ No email/phone — add one with Edit to send their link</div>
                )}
              </div>
              <div className="flex shrink-0 gap-2 text-[11px]">
                {signed ? (
                  <span className="text-gray-400" title="Cancel signatures to edit a signed party">locked (signed)</span>
                ) : (
                  <>
                    <button type="button" className="text-brand hover:underline" onClick={() => startEdit(s)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-red-600 hover:underline"
                      disabled={busy !== null}
                      onClick={async () => {
                        if (!(await confirmDialog(`Remove ${s.signer_name} as a signer? Their signature request is deleted.`, { danger: true, confirmLabel: 'Remove' }))) return;
                        setBusy('rmsigner');
                        setErr(null);
                        try {
                          await removeDocSigner(accessToken, agreementId, s.id);
                          onDone('Signer removed.');
                        } catch (e) {
                          setErr(errMsg(e, 'Could not remove the signer.'));
                        } finally {
                          setBusy(null);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {adding ? (
        <div className="mt-2 rounded border border-surface-border bg-white p-2">
          <div className="grid grid-cols-2 gap-1.5">
            <input className="col-span-2 rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Signing name (required)" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
            <input className="rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Role (optional)" value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} />
            <input className="rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Email (optional)" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
            <input className="col-span-2 rounded-md border border-surface-border px-1.5 py-1 text-xs" placeholder="Phone (optional)" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} />
          </div>
          <div className="mt-1.5 flex gap-2">
            <button
              type="button"
              className="btn-primary !py-0.5 !text-xs"
              disabled={busy !== null || !addForm.name.trim()}
              onClick={async () => {
                setBusy('addsigner');
                setErr(null);
                try {
                  await addDocSigner(accessToken, agreementId, {
                    name: addForm.name.trim(),
                    role_label: addForm.role.trim() || undefined,
                    email: addForm.email.trim() || undefined,
                    phone: addForm.phone.trim() || undefined,
                  });
                  setAddForm({ name: '', role: '', email: '', phone: '' });
                  setAdding(false);
                  onDone('Signer added.');
                } catch (e) {
                  setErr(errMsg(e, 'Could not add the signer.'));
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'addsigner' ? 'Adding…' : 'Add signer'}
            </button>
            <button type="button" className="btn-secondary !py-0.5 !text-xs" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="mt-2 text-xs text-brand hover:underline" onClick={() => setAdding(true)}>
          + Add a signer
        </button>
      )}
    </div>
  );
}

// ── #7 ─────────────────────────────────────────────────────────────────────
function FinalVersion({
  agreementId,
  accessToken,
  editable,
  locked,
  onLoad,
  onSaved,
  setBusy,
  busy,
  setErr,
}: {
  agreementId: string;
  accessToken: string;
  editable: EditableDoc | null;
  locked: boolean;
  onLoad: () => Promise<void>;
  onSaved: (msg?: string) => void;
  setBusy: (v: string | null) => void;
  busy: string | null;
  setErr: (v: string | null) => void;
}) {
  const [show, setShow] = useState(false);
  const [editingBody, setEditingBody] = useState(false);
  const [bodyHtml, setBodyHtml] = useState('');

  useEffect(() => {
    if (editable) setBodyHtml(editable.body_html ?? '');
  }, [editable]);

  const fullHtml = editable
    ? `<!doctype html><html><head><meta charset="utf-8"><style>${editable.styles_css}</style></head><body>${editable.prefix_html ?? ''}${editable.body_html}${editable.suffix_html ?? ''}${editable.signature_html ?? ''}</body></html>`
    : '';

  return (
    <div>
      <div className="section-label">Final version (what parties & the court receive)</div>
      {!show ? (
        <button
          type="button"
          className="mt-1.5 text-xs text-brand hover:underline"
          onClick={async () => {
            setShow(true);
            if (!editable) await onLoad();
          }}
        >
          Preview the final document
        </button>
      ) : (
        <div className="mt-1.5 space-y-2">
          <p className="text-[11px] text-gray-500">
            This is the exact rendered document — caption, case number/title, resolution terms, and signature block.
            Fix the caption identity with the case-number/title and signer controls above.
          </p>
          <div className="h-72">
            <PreviewFrame html={fullHtml} title="Final document" />
          </div>
          {!editingBody ? (
            <button
              type="button"
              className="text-xs text-brand hover:underline disabled:text-gray-400"
              disabled={locked}
              title={locked ? 'Cancel signatures first to edit terms' : undefined}
              onClick={() => setEditingBody(true)}
            >
              ✎ Edit the resolution terms
            </button>
          ) : (
            <div className="rounded border border-surface-border bg-white p-2">
              <div className="mb-1 text-[11px] text-gray-500">Resolution terms (the body of the stipulation):</div>
              <div
                className="min-h-[8rem] rounded border border-surface-border p-2 text-xs focus:border-brand focus:outline-none"
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => setBodyHtml((e.target as HTMLElement).innerHTML)}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
              <div className="mt-1.5 flex gap-2">
                <button
                  type="button"
                  className="btn-primary !py-0.5 !text-xs"
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy('savebody');
                    setErr(null);
                    try {
                      await saveDocBody(accessToken, agreementId, { body_html: bodyHtml });
                      setEditingBody(false);
                      onSaved('Resolution terms saved. Re-send signing links so parties see the update.');
                    } catch (e) {
                      setErr(errMsg(e, 'Could not save the terms.'));
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  {busy === 'savebody' ? 'Saving…' : 'Save terms'}
                </button>
                <button type="button" className="btn-secondary !py-0.5 !text-xs" onClick={() => { setEditingBody(false); setBodyHtml(editable?.body_html ?? ''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
